import { normalize } from 'normalizr';
import mapValues from 'lodash/mapValues';
import moment from 'moment';

import * as schemas from '../entities/schemas';

export const SET_RESERVATION_ID = 'SET_RESERVATION_ID';
export const SET_RESERVATION_STATUS = 'SET_RESERVATION_STATUS';
export const ADD_DEPARTURE = 'ADD_DEPARTURE';
export const ADD_RESERVATION = 'ADD_RESERVATION';
export const SET_ACTIVE_DEPARTURE = 'SET_ACTIVE_DEPARTURE';
export const SET_ACTIVE_CABIN = 'SET_ACTIVE_CABIN';
export const CHANGE_GUEST_FIRST_NAME = 'CHANGE_GUEST_FIRST_NAME';
export const CHANGE_GUEST_LAST_NAME = 'CHANGE_GUEST_LAST_NAME';
export const CHANGE_GUEST_EMAIL = 'CHANGE_GUEST_EMAIL';
export const CHANGE_GUEST_SPECIAL_NEEDS = 'CHANGE_GUEST_SPECIAL_NEEDS';
export const CHANGE_GUEST_EXTRA = 'CHANGE_GUEST_EXTRA';
export const CHANGE_GUEST_AGE = 'CHANGE_GUEST_AGE';
export const CHANGE_GUEST_GENDER = 'CHANGE_GUEST_GENDER';
export const CHANGE_GUEST_NATIONALITY = 'CHANGE_GUEST_NATIONALITY';
export const CHANGE_GUEST_TITLE = 'CHANGE_GUEST_TITLE';
export const CHANGE_GUEST_DATE_OF_BIRTH = 'CHANGE_GUEST_DATE_OF_BIRTH';
export const CHANGE_GUEST_DOCUMENT_TYPE = 'CHANGE_GUEST_DOCUMENT_TYPE';
export const CHANGE_GUEST_DOCUMENT_NUMBER = 'CHANGE_GUEST_DOCUMENT_NUMBER';
export const CHANGE_GUEST_DOCUMENT_ISSUING_COUNTRY = 'CHANGE_GUEST_DOCUMENT_ISSUING_COUNTRY';
export const CHANGE_GUEST_DOCUMENT_EXPIRY_DATE = 'CHANGE_GUEST_DOCUMENT_EXPIRY_DATE';
export const CHANGE_GUEST_ICE_PHONE_NUMBER = 'CHANGE_GUEST_ICE_PHONE_NUMBER';
export const TOGGLE_GUEST_EXPANDED = 'TOGGLE_GUEST_EXPANDED';
export const CHANGE_ALL_GUESTS_LAST_NAME = 'CHANGE_ALL_GUESTS_LAST_NAME';
export const CHANGE_ALL_GUESTS_NATIONALITY = 'CHANGE_ALL_GUESTS_NATIONALITY';
export const CHANGE_GUEST_VISA_REQUIRED = 'CHANGE_GUEST_VISA_REQUIRED';
export const CHANGE_ALL_GUESTS_AGE = 'CHANGE_ALL_GUESTS_AGE';
export const CHANGE_ALL_GUESTS_GENDER = 'CHANGE_ALL_GUESTS_GENDER';
export const CHANGE_ALL_GUESTS_TITLE = 'CHANGE_ALL_GUESTS_TITLE';
export const TOGGLE_ALL_PASSENGERS_EXPANDED = 'TOGGLE_ALL_PASSENGERS_EXPANDED';
export const PUT_GUEST_IN_CABIN = 'PUT_GUEST_IN_CABIN';
export const REMOVE_GUEST_FROM_CABIN = 'REMOVE_GUEST_FROM_CABIN';
export const CLEAR_GUESTS_FROM_CABINS = 'CLEAR_GUESTS_FROM_CABINS';
export const REPLACE_GUEST = 'REPLACE_GUEST';
export const ADD_CABIN_MESSAGE = 'ADD_CABIN_MESSAGE';
export const SAVE_REQUEST = 'SAVE_REQUEST';
export const CHANGE_VEHICLE_REGNO = 'CHANGE_VEHICLE_REGNO';
export const CHANGE_TRAILER_REGNO = 'CHANGE_TRAILER_REGNO';
export const SET_REQUIRED_FIELD = 'SET_REQUIRED_FIELD';
export const UNSET_REQUIRED_FIELD = 'UNSET_REQUIRED_FIELD';
export const SET_VALIDATION_ERROR = 'SET_VALIDATION_ERROR';
export const UNSET_VALIDATION_ERROR = 'UNSET_VALIDATION_ERROR';
export const SAVE_SUCCESS = 'SAVE_SUCCESS';
export const SAVE_FAILED = 'SAVE_FAILED';
export const SAVE_PENDING = 'SAVE_PENDING';
export const SERVER_SAVE_FAILED = 'SERVER_SAVE_FAILED';
export const TOGGLE_HEADER_REQUIRED_FIELD = 'TOGGLE_HEADER_REQUIRED_FIELD';
export const SET_IMPORTED_FILE = 'SET_IMPORTED_FILE';
export const UPDATE_IMPORTED_FILE = 'UPDATE_IMPORTED_FILE';
export const RESET_IMPORTED_FILE = 'RESET_IMPORTED_FILE';
export const SET_CORRECT_COLUMN_FROM_IMPORTED_FILE = 'SET_CORRECT_COLUMN_FROM_IMPORTED_FILE';
export const REMOVE_CORRECT_COLUMN_FROM_IMPORTED_FILE = 'REMOVE_CORRECT_COLUMN_FROM_IMPORTED_FILE';
export const IMPORT_FILE_CHANGE_GUEST = 'IMPORT_FILE_CHANGE_GUEST';
export const SET_STEP_IN_PROCESS_IMPORTED_FILE = 'SET_STEP_IN_PROCESS_IMPORTED_FILE';
export const STEP_BACK_AND_FORTH_IN_PROCESS_IMPORTED_FILE = 'STEP_BACK_AND_FORTH_IN_PROCESS_IMPORTED_FILE';
export const SET_ACTIVE_CABIN_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR = 'SET_ACTIVE_CABIN_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR';
export const SET_ACTIVE_PERSON_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR = 'SET_ACTIVE_PERSON_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR';
export const REMOVE_PERSON_FROM_PERSONS_LINKED_TO_CABIN_IMPORTED_FILE = 'REMOVE_PERSON_FROM_PERSONS_LINKED_TO_CABIN_IMPORTED_FILE';
export const ADD_ANONYMOUS_ID_AND_CABIN_FOR_IMPORT = 'ADD_ANONYMOUS_ID_AND_CABIN_FOR_IMPORT';

export const setReservationId = id => ({
  type: SET_RESERVATION_ID,
  payload: {
    id,
  },
});

export const setReservationStatus = reservationStatus => ({
  type: SET_RESERVATION_STATUS,
  payload: {
    reservationStatus,
  },
});

const guestMaps = [];
const addMapper = (fromKey, toKey, func) => guestMaps.push({ fromKey, toKey, func });

addMapper('dateOfBirth', 'dateOfBirth', datetime => datetime && moment(datetime).format(moment.HTML5_FMT.DATE));

const guestMapper = guest => guestMaps.reduce((mappedGuest, map) => ({
  ...mappedGuest,
  [map.toKey]: map.func(guest[map.fromKey]),
}), {});

const getLinkedCabinIds = (cabins, cabin) =>
  Object.keys(cabins).filter(id => cabins[id].code === cabin.code && cabins[id].instance === cabin.instance).filter(id => id !== cabin.id);

export const addReservation = (reservation) => {
  const { result, entities: { cabins, guests, vehicles, departures, reservations: { [result]: normalizedReservation } } } = normalize(
    reservation,
    schemas.reservations,
  );
  const departureIds = normalizedReservation.departures;

  const cabinsOut = { ...cabins };
  const guestsOut = mapValues(guests, guest => ({
    ...guest,
    ...guestMapper(guest),
  }));
  departureIds.forEach((id) => {
    // add cabin numbers to cabins and selectedCabin to guests
    departures[id].cabins.forEach((cabinId, i) => {
      const cabin = cabinsOut[cabinId] || {};
      const linkedCabins = getLinkedCabinIds(cabins, cabin);
      cabinsOut[cabinId] = {
        ...cabin,
        number: i + 1,
        linkedCabins,
      };
      (cabin.guests || []).forEach((guestId) => {
        const guest = guestsOut[guestId] || {};
        guestsOut[guestId] = {
          ...guest,
          selectedCabin: {
            ...(guest.selectedCabin || {}),
            [id]: cabinId,
          },
        };
      });
    });
    // add guest number to guests
    departures[id].guests.forEach((guestId, i) => {
      const guest = guestsOut[guestId] || {};
      guestsOut[guestId] = {
        ...guest,
        number: {
          ...(guest.number || {}),
          [id]: i + 1,
        },
      };
    });
  });
  return {
    type: ADD_RESERVATION,
    payload: {
      ...normalizedReservation,
      departureIds,
      departures,
      cabins: cabinsOut,
      guests: guestsOut,
      vehicles,
    },
  };
};

export const setActiveDeparture = id => ({
  type: SET_ACTIVE_DEPARTURE,
  payload: {
    id,
  },
});

export const setActiveCabin = (id, departureId) => ({
  type: SET_ACTIVE_CABIN,
  payload: {
    id,
    departureId,
  },
});

const changeProp = (type, propName) => (id, value) => ({
  type,
  payload: { id, [propName]: value },
});

export const togglePassengerExpanded = id => ({
  type: TOGGLE_GUEST_EXPANDED,
  payload: { id },
});

export const changeGuestFirstName = changeProp(CHANGE_GUEST_FIRST_NAME, 'firstName');
export const changeGuestLastName = changeProp(CHANGE_GUEST_LAST_NAME, 'lastName');
export const changeGuestEmail = changeProp(CHANGE_GUEST_EMAIL, 'email');
export const changeGuestSpecialNeeds = changeProp(CHANGE_GUEST_SPECIAL_NEEDS, 'disabilityCode');
export const changeGuestExtra = changeProp(CHANGE_GUEST_EXTRA, 'loyaltyNumber');
export const changeGuestNationality = changeProp(CHANGE_GUEST_NATIONALITY, 'nationality');
export const changeGuestVisaRequired = changeProp(CHANGE_GUEST_VISA_REQUIRED, 'visaRequired');
export const changeGuestAge = changeProp(CHANGE_GUEST_AGE, 'customerCategory');
export const changeGuestGender = changeProp(CHANGE_GUEST_GENDER, 'gender');
export const changeGuestTitle = changeProp(CHANGE_GUEST_TITLE, 'title');
export const changeGuestDateOfBirth = changeProp(CHANGE_GUEST_DATE_OF_BIRTH, 'dateOfBirth');
export const changeGuestDocumentType = changeProp(CHANGE_GUEST_DOCUMENT_TYPE, 'documentType');
export const changeGuestDocumentNumber = changeProp(CHANGE_GUEST_DOCUMENT_NUMBER, 'documentNumber');
export const changeGuestDocumentIssuingCountry = changeProp(CHANGE_GUEST_DOCUMENT_ISSUING_COUNTRY, 'documentIssuingCountry');
export const changeGuestDocumentExpiryDate = changeProp(CHANGE_GUEST_DOCUMENT_EXPIRY_DATE, 'documentExpiryDate');
export const changeIcePhoneNumber = changeProp(CHANGE_GUEST_ICE_PHONE_NUMBER, 'icePhoneNumber');

export const importFileChangeGuest = (guestId, resource, value) => ({
  type: IMPORT_FILE_CHANGE_GUEST,
  payload: {
    guestId,
    resource,
    value,
  },
});

export const setValidationError = (guestId, field) => ({
  type: SET_VALIDATION_ERROR,
  payload: {
    guestId,
    field,
  },
});

export const unsetValidationError = (guestId, field) => ({
  type: UNSET_VALIDATION_ERROR,
  payload: {
    guestId,
    field,
  },
});

export const setRequiredField = (guestId, field) => ({
  type: SET_REQUIRED_FIELD,
  payload: {
    guestId,
    field,
  },
});

export const unsetRequiredField = (guestId, field) => ({
  type: UNSET_REQUIRED_FIELD,
  payload: {
    guestId,
    field,
  },
});

export const putGuestInCabin = (guestId, cabinId) => ({
  type: PUT_GUEST_IN_CABIN,
  payload: {
    guestId,
    cabinId,
  },
});

export const removeGuestFromCabin = (guestId, cabinId) => ({
  type: REMOVE_GUEST_FROM_CABIN,
  payload: {
    guestId,
    cabinId,
  },
});

export const clearGuestFromCabins = departureId => ({
  type: CLEAR_GUESTS_FROM_CABINS,
  payload: {
    departureId,
  },
});

export const changeAllGuestsLastName = lastName => ({
  type: CHANGE_ALL_GUESTS_LAST_NAME,
  payload: {
    lastName,
  },
});

export const changeAllGuestsNationality = nationality => ({
  type: CHANGE_ALL_GUESTS_NATIONALITY,
  payload: {
    nationality,
  },
});

export const changeAllGuestsAge = customerCategory => ({
  type: CHANGE_ALL_GUESTS_AGE,
  payload: {
    customerCategory,
  },
});

export const changeAllGuestsGender = gender => ({
  type: CHANGE_ALL_GUESTS_GENDER,
  payload: {
    gender,
  },
});

export const changeAllGuestsTitle = title => ({
  type: CHANGE_ALL_GUESTS_TITLE,
  payload: {
    title,
  },
});

export const toggleAllPassengersExpanded = () => ({
  type: TOGGLE_ALL_PASSENGERS_EXPANDED,
});

export const replaceGuest = (oldId, newGuest) => ({
  type: REPLACE_GUEST,
  payload: {
    oldId,
    newGuest,
  },
});

export const saveRequest = (id, resource) => ({
  type: SAVE_REQUEST,
  payload: {
    id,
    resource,
  },
});

export const changeVehicleRegNo = (id, regNo) => ({
  type: CHANGE_VEHICLE_REGNO,
  payload: {
    id,
    regNo,
  },
});

export const changeTrailerRegNo = (id, regNo) => ({
  type: CHANGE_TRAILER_REGNO,
  payload: {
    id,
    regNo,
  },
});

export const saveSuccess = (id, resource, response, saveTime) => ({
  type: SAVE_SUCCESS,
  payload: {
    id,
    resource,
    response,
    saveTime,
  },
});

export const saveFailed = (id, resource, response, saveTime) => ({
  type: SAVE_FAILED,
  payload: {
    id,
    resource,
    response,
    saveTime,
  },
});

export const savePending = (id, resource, response, saveTime) => ({
  type: SAVE_PENDING,
  payload: {
    id,
    resource,
    response,
    saveTime,
  },
});

export const serverSaveFailed = (response, saveTime) => ({
  type: SERVER_SAVE_FAILED,
  payload: {
    response,
    saveTime,
  },
});

export const toogleHeaderRequiredField = () => ({
  type: TOGGLE_HEADER_REQUIRED_FIELD,
});

export const setImportFile = data => ({
  type: SET_IMPORTED_FILE,
  payload: {
    data,
  },
});

export const updateImportedFile = file => ({
  type: UPDATE_IMPORTED_FILE,
  payload: {
    file,
  },
});

export const resetImportFile = () => ({
  type: RESET_IMPORTED_FILE,
});

export const setCorrectColumnFromImportedFile = (column, title) => ({
  type: SET_CORRECT_COLUMN_FROM_IMPORTED_FILE,
  payload: {
    column,
    title,
  },
});

export const removeCorrectColumnFromImportedFile = column => ({
  type: REMOVE_CORRECT_COLUMN_FROM_IMPORTED_FILE,
  payload: {
    column,
  },
});

export const setStepInProcessImportedFile = steps => ({
  type: SET_STEP_IN_PROCESS_IMPORTED_FILE,
  payload: {
    steps,
  },
});

export const stepBackAndForthInProcessImportedFile = number => ({
  type: STEP_BACK_AND_FORTH_IN_PROCESS_IMPORTED_FILE,
  payload: {
    number,
  },
});

export const setActiveCabinForImportedFileCabinDistributor = id => ({
  type: SET_ACTIVE_CABIN_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR,
  payload: {
    id,
  },
});

export const setActivePersonForImportedFileCabinDistributor = obj => ({
  type: SET_ACTIVE_PERSON_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR,
  payload: {
    obj,
  },
});

export const removePersonFromPersonsLinkedToCabinImportedFile = cabinId => ({
  type: REMOVE_PERSON_FROM_PERSONS_LINKED_TO_CABIN_IMPORTED_FILE,
  payload: {
    cabinId,
  },
});

export const addAnonymousIdAndCabinForImport = obj => ({
  type: ADD_ANONYMOUS_ID_AND_CABIN_FOR_IMPORT,
  payload: {
    obj,
  },
});



// WEBPACK FOOTER //
// ./src/store/reservation/actions.js